import { useEffect } from 'react';
import { usePortalApi } from '@invoiceflow/portal-core';
import { useAuthStore } from '../stores/useAuthStore';
import { firebaseEnabled, watchIdToken } from '../services/firebase';

/**
 * Reconciles our persisted session with Firebase's on load.
 *
 * Both survive a reload independently — zustand restores the portal user, the
 * Firebase SDK restores the token — and they can disagree. Until they are
 * reconciled, `authReady` is false and ProtectedRoute holds, so a reload never
 * bounces a live session to the login screen.
 */
export function useAuthBootstrap(): void {
  const api = usePortalApi();

  useEffect(() => {
    // Nothing asynchronous to wait for without Firebase.
    if (!firebaseEnabled) {
      useAuthStore.getState().setAuthReady(true);
      return;
    }

    /**
     * Rebuilds a portal session for an already-authenticated Firebase user —
     * the redirect-return path, where nothing local survived. Asking for the
     * tenant links also runs discovery, so a first-time social sign-in
     * completes here too.
     */
    const completeSignIn = async (displayName: string | null, email: string | null) => {
      const { loginWithFirebase, setTenants, logout, setAuthReady } = useAuthStore.getState();

      try {
        const tenants = await api.tenants.listMine();
        if (tenants.length === 0) {
          // Authenticated, but no supplier recognises this address.
          logout();
          return;
        }
        setTenants(tenants);
        loginWithFirebase({
          name: displayName || 'Customer',
          tenantId: tenants[0]!.tenantId,
          ...(email ? { email } : {}),
        });
      } catch {
        logout();
      } finally {
        setAuthReady(true);
      }
    };

    return watchIdToken(firebaseUser => {
      const { user, logout, setAuthReady } = useAuthStore.getState();

      if (firebaseUser) {
        // A Firebase session with no portal user of our own is the normal state
        // on returning from a social sign-in redirect, so rebuild it from the
        // backend rather than signing out.
        if (!user) {
          void completeSignIn(firebaseUser.displayName, firebaseUser.email);
          return;
        }
        setAuthReady(true);
        return;
      }

      // Firebase says signed out, so we are.
      if (user) logout();
      setAuthReady(true);
    });
  }, [api]);
}
